import {
  autoDimensionHandleParentEntityType,
  getAutoDimensionEffectiveParentHandles,
} from "building-types/handles/autoDimensionHandleTools";
import type {
  DocEntityHandle,
  LevelHandle,
  RoomHandle,
  ViewTemplateHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { ViewHandle } from "building-types/handles/viewHandleTypes";
import type { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { DocEntityType } from "../doc/docEntityTypes";
import type {
  AutoDimensionSelector,
  SelectorType,
  ViewOverridableEntityType,
  ViewOverridableHandle,
  ViewOverridableViewHandle,
  ViewOverridableViewType,
} from "./selectorTypes";
import type { ViewSelectable, ViewSelectableEntityType, ViewSelectableAndUnselectable } from "./viewSelectorTypes";

export function viewOverridableHandle(handle: DocEntityHandle): handle is ViewOverridableHandle {
  return handle.type in viewOverridableEntityTypes;
}

export function viewOverridableViewHandle(
  handle: ViewHandle | ViewTemplateHandle,
): handle is ViewOverridableViewHandle {
  return handle.type in viewOverridableViewTypes;
}

export const viewOverridableEntityTypes: {
  [key in ViewOverridableEntityType]: boolean;
} = {
  AUTO_DIMENSION: true,
};

export const viewOverridableViewTypes: {
  [key in ViewOverridableViewType]: true;
} = {
  PLAN_VIEW: true,
  ELEVATION_VIEW: true,
  SECTION_VIEW: true,
  VIEW_3D: true,
  GENERATED_PAGE_VIEW: true,
  VIEW_TEMPLATE: true,
};

export const BuiltinViewablePlaceholderId = "builtInViewPlaceholder";
export const builtinViewablePlaceholder: ViewSelectable = {
  entityType: "BUILTIN_VIEW_PLACEHOLDER",
  id: BuiltinViewablePlaceholderId,
};

export const SelectorTypeToViewTypes: { [key in SelectorType]: { [key in ViewOverridableViewType]?: true } } = {
  AUTO_DIMENSION: { ["PLAN_VIEW"]: true, ["GENERATED_PAGE_VIEW"]: true },
};

export const SelectorTypeToBuiltinPlanViewTypes: { [key in SelectorType]: { [key in BuiltinPlanViewType]?: true } } = {
  AUTO_DIMENSION: { ["FLOOR"]: true, ["ELECTRICAL"]: true, ["FOUNDATION"]: true },
};

/**
 * The indirection here could be overkill. But keeping the indirection in case a mapping needs to be done.
 */
export const SelectorTypeToPlanGeomDocEntityHandleType: { [key in SelectorType]: { [key in DocEntityType]?: true } } = {
  AUTO_DIMENSION: { ["AUTO_DIMENSION"]: true },
};

export const SelectorTypeToSheetGeometryCategories: {
  [key in SelectorType]: { [key in SheetGeometryCategory]?: boolean };
} = {
  AUTO_DIMENSION: {
    WALL_EXTERIOR_DIM_GROUP: true,
    WALL_EXTERIOR_DIM_LABEL: true,
    WALL_EXTERIOR_DIM_LINE: true,
    WALL_EXTERIOR_DIM_LINEWORK: true,
    WALL_EXTERIOR_DIM_TICK: true,
    WALL_EXTERIOR_DIM_WITNESS: true,
    WALL_INTERIOR_DIM_WITNESS: true,
    WALL_INTERIOR_DIM_GROUP: true,
    WALL_INTERIOR_DIM_LABEL: true,
    WALL_INTERIOR_DIM_LINE: true,
    WALL_INTERIOR_DIM_LINEWORK: true,
    WALL_INTERIOR_DIM_TICK: true,
    FOUNDATION_DIM_GROUP: true,
    FOUNDATION_DIM_LABEL: true,
    FOUNDATION_DIM_LINE: true,
    FOUNDATION_DIM_LINEWORK: true,
    FOUNDATION_DIM_TICK: true,
    FOUNDATION_DIM_WITNESS: true,
  },
};

export const ViewSelectableEntityTypes: {
  [key in ViewSelectableEntityType]: true;
} = {
  VIEW_3D: true,
  PLAN_VIEW: true,
  SECTION_VIEW: true,
  ELEVATION_VIEW: true,
  BUILTIN_VIEW_PLACEHOLDER: true,
  VIEW_TEMPLATE: true,
  VIEWPORT: true,
};

export function isViewSelectable(v: ViewSelectableAndUnselectable): v is ViewSelectable {
  return v.entityType in ViewSelectableEntityTypes;
}

export function getAutoDimensionSelectorEffectiveParentHandles<T extends RoomHandle | LevelHandle>(
  selector: AutoDimensionSelector,
  ofEntityType: T["type"],
): T[] {
  const parentHandles: T[] = [];

  if (selector.parentId) {
    const entityType = autoDimensionHandleParentEntityType(selector.entityHandle);
    if (entityType === ofEntityType) {
      parentHandles.push({ type: entityType, id: selector.parentId } as T);
    }
  }

  for (const provenanceHandle of getAutoDimensionEffectiveParentHandles(selector.entityHandle)) {
    if (provenanceHandle.type === ofEntityType) {
      parentHandles.push(provenanceHandle as T);
    }
  }

  return parentHandles;
}
